import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ListChecks,
  Loader2,
  MapPin,
  MinusCircle,
  ShieldOff,
  SlashIcon,
  XCircle,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import { decideAwpFinding } from "./api";
import {
  AwpFinding,
  AwpFindingStatus,
  AwpRun,
  AwpSeverity,
  CatalogueRuleResult,
  CatalogueRuleStatus,
  RULE_STATUS_LABELS,
  STATUS_LABELS,
  statusOf,
} from "./types";

interface AwpFindingsProps {
  projectId: string;
  run: AwpRun | null;
  admin?: boolean;
  onRun: (run: AwpRun) => void;
  onGoToReview: () => void;
}

const SEVERITY_CLASS: Record<AwpSeverity, string> = {
  Critical: "vr-crit",
  Major: "vr-major",
  Minor: "vr-muted",
};

/** Pass is green, findings red, everything else deliberately not green. */
const RULE_STATUS_CLASS: Record<CatalogueRuleStatus, string> = {
  pass: "vr-pass-ico",
  findings: "vr-crit",
  "no-data": "vr-major",
  "not-applicable": "vr-muted",
  "not-automated": "vr-muted",
};

const RuleStatusIcon = ({ status }: { status: CatalogueRuleStatus }) => {
  if (status === "pass") return <CheckCircle2 size={13} className="vr-pass-ico" />;
  if (status === "findings") return <AlertTriangle size={13} className="vr-crit" />;
  if (status === "no-data") return <AlertTriangle size={13} className="vr-major" />;
  if (status === "not-applicable") return <SlashIcon size={13} className="vr-muted" />;
  return <MinusCircle size={13} className="vr-muted" />;
};

type RuleFilter = CatalogueRuleStatus | "all" | "tested";
type Filter = "open" | "all" | AwpFindingStatus;

/**
 * The run's result: every rule of the standard, with the findings sitting under the rule they breach.
 *
 * ONE SCREEN, NOT TWO. A findings list on its own is the dangerous artefact — three findings read
 * as "nearly clean" whatever the denominator is. Putting them inside the rule register means the
 * reviewer cannot see the findings without also seeing how many rules produced them and how many
 * were never examined at all.
 *
 * EVERY FINDING CARRIES BOTH SIDES. An AWP finding is a disagreement between two documents, so the
 * row shows what this package says and what the other one says, each with the table and row it was
 * read from. Nothing here asks the reviewer to take the engine's word for it — the point of a
 * deterministic check is that the evidence is the finding.
 *
 * A DISMISSAL NEEDS A REASON. Accept is a decision anybody can read later from the finding itself;
 * "false positive" and "waived" are decisions about something the documents still disagree on, and
 * six months on, the reason is the only thing that distinguishes a judgement from an oversight. The
 * server refuses them without one.
 */
const AwpFindings = ({ projectId, run, admin = false, onRun, onGoToReview }: AwpFindingsProps) => {
  // Opens on the rules this engine actually checked. The rest are a filter away, not a wall the
  // reviewer has to scroll past to reach the work.
  const [ruleFilter, setRuleFilter] = useState<RuleFilter>("tested");
  const [domain, setDomain] = useState<string>("all");
  const [expanded, setExpanded] = useState<string[]>([]);
  const [filter, setFilter] = useState<Filter>("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const findings = useMemo(() => run?.findings ?? [], [run]);
  const ruleResults = useMemo(() => run?.ruleResults ?? [], [run]);

  const counts = useMemo(() => {
    const out = { open: 0, accepted: 0, "false-positive": 0, waived: 0 } as Record<AwpFindingStatus, number>;
    for (const f of findings) out[statusOf(f)] += 1;
    return out;
  }, [findings]);

  const domains = useMemo(() => {
    const seen = new Map<string, { code: string; name: string; total: number; failed: number }>();
    for (const r of ruleResults) {
      if (!seen.has(r.domainCode)) {
        seen.set(r.domainCode, { code: r.domainCode, name: r.domain, total: 0, failed: 0 });
      }
      const d = seen.get(r.domainCode)!;
      d.total += 1;
      if (r.status === "findings") d.failed += 1;
    }
    return [...seen.values()];
  }, [ruleResults]);

  const showingRules = useMemo(
    () =>
      ruleResults.filter((r) => {
        if (domain !== "all" && r.domainCode !== domain) return false;
        if (ruleFilter === "all") return true;
        if (ruleFilter === "tested") return r.status === "pass" || r.status === "findings";
        return r.status === ruleFilter;
      }),
    [ruleResults, domain, ruleFilter],
  );

  /** The findings a given rule row owns — those raised by a check that implements it. */
  const findingsFor = (rule: CatalogueRuleResult) =>
    findings.filter((f) => rule.checks.includes(f.ruleId));

  const showingFindings = useMemo(
    () => (filter === "all" ? findings : findings.filter((f) => statusOf(f) === filter)),
    [findings, filter],
  );

  const decide = async (finding: AwpFinding, status: AwpFindingStatus) => {
    if ((status === "false-positive" || status === "waived") && !note.trim()) {
      setError("Say why — a dismissal needs a reason.");
      return;
    }
    setBusy(finding.id);
    setError(null);
    try {
      onRun(await decideAwpFinding(projectId, finding.id, status, note.trim()));
      setNote("");
      setOpenId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record the decision.");
    } finally {
      setBusy(null);
    }
  };

  /** One finding, with both sides and a decision. Used inside a rule row and in the flat list. */
  const FindingCard = ({ finding }: { finding: AwpFinding }) => {
    const status = statusOf(finding);
    const isOpen = openId === finding.id;
    return (
      <div className={`vr-finding${status !== "open" ? " vr-finding-closed" : ""}`}>
        <div className="vr-finding-head">
          <span className={`vr-sev-pill ${SEVERITY_CLASS[finding.severity]}`}>{finding.severity}</span>
          <div className="vr-finding-names">
            <span className="vr-finding-pkg">
              {finding.packageType} {finding.packageNo}
            </span>
            <span className="vr-finding-cat">{finding.category}</span>
          </div>
          {status !== "open" && <span className="vr-finding-status">{STATUS_LABELS[status]}</span>}
        </div>

        <p className="vr-finding-detail">{finding.detail}</p>

        {/* Both sides, with where each was read. This IS the proof — a deterministic
            finding that does not show its two values is no better than a guess. */}
        <div className="vr-finding-sides">
          <div className="vr-finding-side vr-finding-side-found">
            <span className="vr-finding-side-pkg">{finding.found.packageNo}</span>
            <span className="vr-finding-side-value">{finding.found.value}</span>
            {finding.found.evidence && (
              <span className="vr-finding-side-where">
                <MapPin size={10} /> {finding.found.evidence.heading}, row {finding.found.evidence.row}
              </span>
            )}
          </div>
          {finding.against.map((side, i) => (
            <div key={`${side.packageNo}-${i}`} className="vr-finding-side">
              <span className="vr-finding-side-pkg">{side.packageNo}</span>
              <span className="vr-finding-side-value">{side.value}</span>
              {side.evidence && (
                <span className="vr-finding-side-where">
                  <MapPin size={10} /> {side.evidence.heading}, row {side.evidence.row}
                </span>
              )}
            </div>
          ))}
        </div>

        {finding.reviewNote && (
          <p className="vr-finding-note">
            {STATUS_LABELS[status]}: {finding.reviewNote}
          </p>
        )}

        {!admin && (
          <div className="vr-finding-actions">
            {isOpen ? (
              <>
                <input
                  className="vr-input"
                  placeholder="Why? (required to dismiss)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  autoFocus
                />
                <button
                  className="vr-btn vr-btn-sm vr-btn-glow"
                  onClick={() => void decide(finding, "accepted")}
                  disabled={busy === finding.id}
                >
                  {busy === finding.id ? <Loader2 size={12} className="vr-spin-inline" /> : <Check size={12} />}{" "}
                  Accept
                </button>
                <button
                  className="vr-btn vr-btn-sm vr-btn-outline"
                  onClick={() => void decide(finding, "false-positive")}
                  disabled={busy === finding.id}
                >
                  <XCircle size={12} /> False positive
                </button>
                <button
                  className="vr-btn vr-btn-sm vr-btn-outline"
                  onClick={() => void decide(finding, "waived")}
                  disabled={busy === finding.id}
                >
                  <ShieldOff size={12} /> Waive
                </button>
                <button
                  className="vr-rowbtn"
                  onClick={() => {
                    setOpenId(null);
                    setNote("");
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="vr-btn vr-btn-sm vr-btn-outline"
                onClick={() => {
                  setOpenId(finding.id);
                  setNote(finding.reviewNote ?? "");
                  setError(null);
                }}
              >
                {status === "open" ? "Decide" : "Change the decision"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!run) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <ListChecks size={28} />
          </div>
          <h2 className="vr-h-sec">Nothing checked yet</h2>
          <p className="vr-muted" style={{ maxWidth: 460 }}>
            Run the Review Engine and the result appears here: every rule of the standard, with the
            findings under the rule they breach.
          </p>
          <button className="vr-btn vr-btn-outline" onClick={onGoToReview}>
            Go to the Review Engine <ArrowRight size={14} />
          </button>
        </div>
      </GlowCard>
    );
  }

  const cov = run.coverage;

  return (
    <div className="vr-awp-findings">
      {error && <div className="vr-form-error">{error}</div>}

      {/* The numbers are about the work that was done. The scope of the engine is a fact worth
          stating once, in plain text underneath — not a red number competing with the findings. */}
      {cov && (
        <div className="vr-grid vr-grid-4">
          {[
            { label: "Rules checked", value: cov.automated, tone: "" },
            { label: "Passed", value: cov.passed, tone: cov.passed ? "vr-pass-ico" : "" },
            { label: "With findings", value: cov.failed, tone: cov.failed ? "vr-crit" : "" },
            { label: "Packages checked", value: run.packageIds.length, tone: "" },
          ].map((tile) => (
            <GlowCard key={tile.label} hover={false} className="vr-stat">
              <p className={`vr-stat-value ${tile.tone}`}>{tile.value}</p>
              <p className="vr-stat-label">{tile.label}</p>
            </GlowCard>
          ))}
        </div>
      )}

      {cov && (
        <p className="vr-muted vr-awp-scope">
          {cov.automated} of the {cov.total} rules in{" "}
          {run.ruleSets?.map((s) => s.docNo || s.title).join(", ") || "the standard"} have an
          automated check here. The rest need data outside the work packages — the 3D model, the
          schedule, the warehouse — or a judgement rather than a comparison.
          {cov.noData > 0 && ` ${cov.noData} had a check but nothing in these packages to run it on.`}
        </p>
      )}

      {/* ── The register: every rule, findings nested under the rule they breach ── */}
      {ruleResults.length > 0 && (
        <GlowCard className="vr-table-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <BookOpen size={14} />
              <h3>
                {run.ruleSets?.map((s) => s.docNo || s.title).join(" · ")}{" "}
                <span className="vr-cell-code">
                  ({showingRules.length}/{ruleResults.length})
                </span>
              </h3>
            </div>
            {findings.length > 0 && (
              <button
                className="vr-linkbtn"
                onClick={() => {
                  setRuleFilter("findings");
                  setExpanded(
                    ruleResults.filter((r) => r.status === "findings").map((r) => r.id),
                  );
                }}
              >
                Open the {findings.length} {findings.length === 1 ? "finding" : "findings"}
              </button>
            )}
          </div>

          <div className="vr-chips vr-awp-filters">
            <select
              className="vr-input vr-input-sm"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            >
              <option value="all">All domains ({ruleResults.length})</option>
              {domains.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.code} · {d.name} ({d.total}){d.failed ? ` — ${d.failed} with findings` : ""}
                </option>
              ))}
            </select>
            {(
              [
                ["tested", "Checked"],
                ["findings", "Findings"],
                ["pass", "Pass"],
                ["not-automated", "No automated check"],
                ["no-data", "Nothing to test"],
                ["all", "All"],
              ] as [RuleFilter, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`vr-chip-btn${ruleFilter === key ? " vr-active" : ""}`}
                onClick={() => setRuleFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="vr-section-list">
            {showingRules.length === 0 && <div className="vr-empty">Nothing in this group.</div>}
            {showingRules.map((r) => {
              const mine = r.status === "findings" ? findingsFor(r) : [];
              const isOpen = expanded.includes(r.id);
              return (
                <div key={r.id}>
                  <div
                    className={`vr-rule-row${r.status === "findings" ? " vr-needs" : ""}${
                      mine.length ? " vr-rule-row-open" : ""
                    }`}
                    onClick={
                      mine.length
                        ? () => setExpanded((prev) => (isOpen ? prev.filter((x) => x !== r.id) : [...prev, r.id]))
                        : undefined
                    }
                  >
                    {mine.length ? (
                      isOpen ? (
                        <ChevronDown size={13} className="vr-crit" />
                      ) : (
                        <ChevronRight size={13} className="vr-crit" />
                      )
                    ) : (
                      <RuleStatusIcon status={r.status} />
                    )}
                    <span className="vr-cell-code vr-rule-code">{r.code}</span>
                    <span className="vr-rule-body">
                      <span className="vr-rule-title">{r.title}</span>
                      <span className="vr-rule-req">{r.requirement}</span>
                      {r.reason && <em className="vr-awp-skip">{r.reason}</em>}
                    </span>
                    <span className="vr-rule-meta">
                      <span className={`vr-sev-pill ${RULE_STATUS_CLASS[r.status]}`}>
                        {RULE_STATUS_LABELS[r.status]}
                      </span>
                      <span className="vr-cell-code">
                        {r.status === "pass" || r.status === "findings"
                          ? `${r.compared} compared · ${r.raised} raised`
                          : `${r.gate} · ${r.severity}`}
                      </span>
                    </span>
                  </div>
                  {isOpen && mine.length > 0 && (
                    <div className="vr-rule-findings">
                      {mine.map((f) => (
                        <FindingCard key={f.id} finding={f} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </GlowCard>
      )}

      {/* No standard was picked for the run, so there is no register to nest findings in. */}
      {ruleResults.length === 0 && findings.length === 0 && (
        <GlowCard hover={false}>
          <div className="vr-soon">
            <div className="vr-soon-icon">
              <Check size={28} />
            </div>
            <h2 className="vr-h-sec">No disagreements found</h2>
            <p className="vr-muted" style={{ maxWidth: 520 }}>
              The {run.packageIds.length} {run.packageIds.length === 1 ? "package" : "packages"}{" "}
              checked agree with each other on every revision, quantity and date the rules compare.
              No standard was selected for this run, so this says nothing about coverage of one.
            </p>
            <button className="vr-btn vr-btn-outline" onClick={onGoToReview}>
              Pick a standard and check again <ArrowRight size={14} />
            </button>
          </div>
        </GlowCard>
      )}

      {ruleResults.length === 0 && findings.length > 0 && (
        <GlowCard className="vr-table-card" hover={false}>
          <div className="vr-table-head">
            <div className="vr-card-head">
              <ListChecks size={14} />
              <h3>Findings ({findings.length})</h3>
            </div>
            <div className="vr-chips">
              {(
                [
                  ["open", `Open (${counts.open})`],
                  ["accepted", `Accepted (${counts.accepted})`],
                  ["false-positive", `False positive (${counts["false-positive"]})`],
                  ["waived", `Waived (${counts.waived})`],
                  ["all", `All (${findings.length})`],
                ] as [Filter, string][]
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`vr-chip-btn${filter === key ? " vr-active" : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="vr-finding-list">
            {showingFindings.length === 0 && <div className="vr-empty">Nothing in this group.</div>}
            {showingFindings.map((f) => (
              <FindingCard key={f.id} finding={f} />
            ))}
          </div>
        </GlowCard>
      )}
    </div>
  );
};

export default AwpFindings;
